import { Backdrop, Box, Stack, Typography, useTheme } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import '../../../components/DataGrid/DataGridContext.css'

const CYCLE_DURATION = 2 // seconds

// Build CSS @keyframes for each block — each appears at its stagger point, stays, then fades with group
function buildKeyframeCSS(name: string, index: number) {
  const appearAt = index * 10
  const solidAt = appearAt + 8
  return `
    @keyframes ${name} {
      0%              { opacity: 0.08; }
      ${appearAt}%    { opacity: 0.08; }
      ${solidAt}%     { opacity: 1; }
      75%             { opacity: 1; }
      90%             { opacity: 0.08; }
      100%            { opacity: 0.08; }
    }
  `
}

// Pre-build all 6 keyframe CSS rules
const BLOCK_NAMES = ['gll-b0', 'gll-b1', 'gll-b2', 'gll-b3', 'gll-b4', 'gll-b5']
const keyframeCSS = BLOCK_NAMES.map((name, i) => buildKeyframeCSS(name, i)).join('\n')

export interface SyncStep {
  label: string
  done: boolean
}

/**
 * Staggered step display — even if all steps complete at once,
 * they visually turn green one at a time for better feedback.
 */
const StaggeredSteps = ({ steps }: { steps: SyncStep[] }) => {
  const [visibleDone, setVisibleDone] = useState<Set<number>>(new Set())
  const queueRef = useRef<number[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Find newly completed steps that aren't visually shown yet
    const newlyDone = steps
      .map((s, i) => (s.done && !visibleDone.has(i) ? i : -1))
      .filter(i => i >= 0)

    if (newlyDone.length === 0) return

    // Add to queue
    queueRef.current = [...queueRef.current, ...newlyDone.filter(i => !queueRef.current.includes(i))]

    // Process queue with stagger
    const processQueue = () => {
      if (queueRef.current.length === 0) return
      const next = queueRef.current.shift()!
      setVisibleDone(prev => new Set([...prev, next]))
      if (queueRef.current.length > 0) {
        timerRef.current = setTimeout(processQueue, 400)
      }
    }

    if (!timerRef.current) {
      processQueue()
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [steps, visibleDone])

  return (
    <Stack gap={0.5} sx={{ mt: 1, alignItems: 'flex-start', minWidth: 220 }}>
      {steps.map((step, i) => {
        const isDone = visibleDone.has(i)
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isDone ? '#43A047' : 'text.disabled',
                transition: 'background-color 0.4s ease',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                color: isDone ? 'text.secondary' : 'text.disabled',
                transition: 'color 0.4s ease',
                fontWeight: isDone ? 400 : 300,
              }}
            >
              {step.label}{isDone ? '' : '...'}
            </Typography>
          </Box>
        )
      })}
    </Stack>
  )
}

interface GroboticLogoLoaderProps {
  size?: number
  message?: string
  overlay?: boolean
  open?: boolean
  steps?: SyncStep[]
}

const GROBxLoader = ({ size = 80, message, overlay = false, open = true, steps }: GroboticLogoLoaderProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const blockStyle = (index: number): React.CSSProperties => ({
    opacity: open ? 0.08 : 1,
    ...(open && { animation: `${BLOCK_NAMES[index]} ${CYCLE_DURATION}s ease-in-out infinite` }),
  })

  const letters = message?.split('') ?? []
  const dots = ['.', '.', '.']

  const logo = (
    <Stack
      gap={1.5}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'transparent',
      }}
    >
      <svg viewBox="1 6.5 22 17.5" width={size} height={size} fill="none" aria-hidden="true">
        <style>{keyframeCSS}</style>
        {/* Column 1: Green (single block) */}
        <rect x="1.5" y="17.5" width="7" height="6" rx="1.2" fill="#43A047" style={blockStyle(0)} />
        {/* Column 2: bottom-up — Blue first, then Dark blue */}
        <rect x="10" y="18" width="6" height="5.5" rx="1.2" fill="#0066DC" style={blockStyle(1)} />
        <rect
          x="10"
          y="11.5"
          width="6"
          height="5"
          rx="1.2"
          fill={isDark ? '#1D4E89' : '#1D4E89'}
          style={blockStyle(2)}
        />
        {/* Column 3: bottom-up — Yellow, Orange, Red */}
        <rect x="17.5" y="19" width="5" height="4.5" rx="1" fill="#FFB703" style={blockStyle(3)} />
        <rect x="17.5" y="13" width="5" height="4.5" rx="1" fill="#F29112" style={blockStyle(4)} />
        <rect x="17.5" y="7" width="5" height="4.5" rx="1" fill="#D95F55" style={blockStyle(5)} />
      </svg>

      {letters.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {letters.map((char, index) => (
            <Typography
              key={index}
              className="animated-letter"
              style={{ animationDelay: `${index * 0.1}s`, fontSize: 18 }}
            >
              {char}
            </Typography>
          ))}
          {dots.map((char, index) => (
            <Typography
              key={`dot-${index}`}
              className="animated-letter-end"
              style={{ animationDelay: `${index * 0.2}s`, fontSize: 20 }}
            >
              {char}
            </Typography>
          ))}
        </Box>
      )}

      {steps && steps.length > 0 && <StaggeredSteps steps={steps} />}
    </Stack>
  )

  if (overlay) {
    return (
      <Backdrop sx={{ zIndex: theme => theme.zIndex.drawer + 1 }} open={open}>
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 150,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'transparent',
            backdropFilter: 'blur(3px)',
          }}
        >
          {logo}
        </Stack>
      </Backdrop>
    )
  }

  return logo
}

export default GROBxLoader
